import { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, ArrowLeft, ArrowRight, Calendar, MapPin, Users, Loader2, Video,
  CheckCircle2, AlertCircle, Mail, Phone, Building2, MessageSquare, User,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au';

interface Presenter {
  name: string;
  title?: string | null;
  imageUrl?: string | null;
  bio?: string | null;
}

interface Workshop {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  presenterName: string | null;
  presenterTitle: string | null;
  presenters: Presenter[];
  coverImageUrl: string | null;
  startsAt: string;
  endsAt: string | null;
  locationType: string;
  location: string | null;
  capacity: number | null;
  status: string;
  topics: string[];
  agenda: { time: string; title: string }[];
  registrantCount: number;
}

function useCountdown(target: string | null) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    if (!target) return null;
    const diff = new Date(target).getTime() - now;
    if (diff <= 0) return { done: true, days: 0, hours: 0, minutes: 0, seconds: 0 };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { done: false, days, hours, minutes, seconds };
  }, [target, now]);
}

function fmtFullWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function WorkshopDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [ws, setWs] = useState<Workshop | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    axios.get<{ data: Workshop }>(`${API_URL}/public/workshops/${slug}`)
      .then(r => setWs(r.data.data))
      .catch(e => setLoadError(e?.response?.data?.error || 'Workshop not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const countdown = useCountdown(ws?.startsAt || null);
  const isPast = !!countdown && countdown.done;
  const isFull = ws?.capacity != null && ws.registrantCount >= ws.capacity;

  return (
    <div className="min-h-screen bg-[#262635] text-white relative overflow-x-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-1/4 left-1/4 w-[600px] h-[600px] bg-[#5048e5]/10 rounded-full blur-[180px]" />
        <div className="absolute bottom-1/4 right-1/5 w-[500px] h-[500px] bg-[#7c3aed]/12 rounded-full blur-[150px]" />
      </div>

      <header className="relative z-10 px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#7c75f0]" />
          <span className="text-base tracking-[0.15em] font-light">RONIDOC<span className="font-semibold">AI</span></span>
        </Link>
        <Link to="/workshops" className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-zinc-400 hover:text-white transition">
          <ArrowLeft className="h-3.5 w-3.5" /> All workshops
        </Link>
      </header>

      <main className="relative z-10 px-6 md:px-12 lg:px-24 pb-24">
        {loading && (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>
        )}

        {loadError && (
          <div className="max-w-xl mx-auto py-20 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-rose-400" />
            <p className="mt-4 text-sm font-medium">{loadError}</p>
            <Link to="/workshops" className="inline-flex items-center gap-1.5 mt-6 text-xs tracking-[0.15em] uppercase text-[#7c75f0] hover:text-white transition">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to workshops
            </Link>
          </div>
        )}

        {ws && (
          <div className="max-w-6xl mx-auto pt-8 md:pt-12">
            {/* COVER IMAGE - natural aspect ratio, never cropped */}
            {ws.coverImageUrl && (
              <div className="mb-10 overflow-hidden rounded-3xl border border-zinc-800/60 bg-zinc-900">
                <img src={ws.coverImageUrl} alt="" className="block w-full h-auto" />
              </div>
            )}

            {/* HERO */}
            <div>
              <p className="text-[11px] md:text-[13px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">
                {isPast ? 'Past Workshop · Recording available on request' : 'Live Workshop · Free'}
              </p>
              <h1 className="text-[clamp(2rem,6vw,4.5rem)] font-extralight leading-[1.05] tracking-tight">
                {ws.title}
              </h1>
              {ws.subtitle && (
                <p className="mt-4 text-lg md:text-xl text-zinc-300 max-w-3xl">{ws.subtitle}</p>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-zinc-400">
                <span className="inline-flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#7c75f0]" /> {fmtFullWhen(ws.startsAt)}
                </span>
                <span className="inline-flex items-center gap-2">
                  {ws.locationType === 'online' ? <Video className="h-4 w-4 text-[#7c75f0]" /> : <MapPin className="h-4 w-4 text-[#7c75f0]" />}
                  {ws.locationType === 'online' ? 'Online · joining link sent on registration' : ws.location || 'TBA'}
                </span>
                {ws.capacity != null && (
                  <span className="inline-flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#7c75f0]" /> {ws.registrantCount} / {ws.capacity} registered
                  </span>
                )}
              </div>
            </div>

            {/* COUNTDOWN */}
            {countdown && !countdown.done && (
              <CountdownStrip {...countdown} />
            )}

            {/* PRESENTERS - placed under the countdown timer */}
            {ws.presenters && ws.presenters.length > 0 && (
              <div className="mt-10">
                <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">Presented by</p>
                <div className={`grid grid-cols-1 ${ws.presenters.length > 1 ? 'sm:grid-cols-2' : ''} gap-4`}>
                  {ws.presenters.map((p, i) => (
                    <div key={i} className="flex items-start gap-4 border border-zinc-800/60 bg-zinc-900/30 rounded-2xl p-4 backdrop-blur-md">
                      {p.imageUrl ? (
                        <img src={p.imageUrl} alt={p.name} className="h-16 w-16 rounded-full object-cover flex-shrink-0 ring-2 ring-[#7c75f0]/40" />
                      ) : (
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#5048e5] to-[#7c3aed] text-lg font-semibold flex-shrink-0">
                          {p.name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-semibold">{p.name}</p>
                        {p.title && <p className="text-sm text-zinc-500">{p.title}</p>}
                        {p.bio && <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{p.bio}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TWO-COLUMN body */}
            <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10 lg:gap-16">
              {/* LEFT - content */}
              <div className="space-y-10">
                {ws.description && (
                  <section>
                    <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">About this session</p>
                    <div
                      className="prose prose-invert prose-zinc max-w-none text-zinc-300
                                 prose-headings:text-white prose-headings:font-semibold
                                 prose-a:text-[#7c75f0] prose-strong:text-white
                                 prose-li:my-1 prose-p:leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: ws.description }}
                    />
                  </section>
                )}

                {ws.topics && ws.topics.length > 0 && (
                  <section>
                    <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">What you'll learn</p>
                    <ul className="space-y-3">
                      {ws.topics.map((t, i) => (
                        <li key={i} className="flex gap-3">
                          <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#7c75f0] mt-0.5" />
                          <span className="text-base text-zinc-300">{t}</span>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {ws.agenda && ws.agenda.length > 0 && (
                  <section>
                    <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-4">Agenda</p>
                    <div className="border border-zinc-800 rounded-2xl divide-y divide-zinc-800/60">
                      {ws.agenda.map((item, i) => (
                        <div key={i} className="flex gap-4 px-5 py-4">
                          <span className="font-mono text-xs text-[#c4b5fd] w-16 flex-shrink-0 mt-1">{item.time}</span>
                          <span className="text-sm text-zinc-200">{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

              </div>

              {/* RIGHT - registration form, sticky */}
              <aside className="lg:sticky lg:top-8 lg:self-start">
                {isPast ? (
                  <div className="border border-zinc-600/60 bg-[#363648] backdrop-blur-md rounded-2xl p-6 text-center shadow-xl shadow-black/20">
                    <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-400 mb-3">This session has ended</p>
                    <p className="text-sm text-zinc-300">Want to be notified when we run it again?</p>
                    <Link to="/demo" className="mt-4 inline-flex items-center gap-2 bg-[#5048e5] hover:bg-[#5f58e8] text-white text-[11px] tracking-[0.15em] uppercase px-5 py-2.5 rounded-full transition">
                      Book a demo instead <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                ) : (
                  <RegistrationForm slug={ws.slug} isFull={isFull} onCount={() => setWs(w => w ? { ...w, registrantCount: w.registrantCount + 1 } : w)} />
                )}
              </aside>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function CountdownStrip({ days, hours, minutes, seconds }: { days: number; hours: number; minutes: number; seconds: number }) {
  const Cell = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center min-w-[80px]">
      <span className="font-extralight text-[clamp(2.5rem,7vw,5rem)] tabular-nums leading-none">
        {String(value).padStart(2, '0')}
      </span>
      <span className="mt-2 text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-zinc-500">{label}</span>
    </div>
  );
  return (
    <div className="mt-12 border border-zinc-800 bg-gradient-to-br from-[#5048e5]/10 via-zinc-900/30 to-transparent backdrop-blur-md rounded-3xl px-6 py-8 md:px-10 md:py-10">
      <p className="text-center text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-6">Starts in</p>
      <div className="flex justify-center items-start gap-3 md:gap-8">
        <Cell value={days} label="Days" />
        <span className="font-extralight text-[clamp(2rem,6vw,4rem)] text-zinc-700 leading-none">:</span>
        <Cell value={hours} label="Hours" />
        <span className="font-extralight text-[clamp(2rem,6vw,4rem)] text-zinc-700 leading-none">:</span>
        <Cell value={minutes} label="Min" />
        <span className="font-extralight text-[clamp(2rem,6vw,4rem)] text-zinc-700 leading-none">:</span>
        <Cell value={seconds} label="Sec" />
      </div>
    </div>
  );
}

function RegistrationForm({ slug, isFull, onCount }: { slug: string; isFull: boolean; onCount: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', firm: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm(prev => ({ ...prev, [k]: v }));
  const canSubmit = form.name.trim() && form.email.trim() && !submitting;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/public/workshops/${slug}/register`, {
        name: form.name.trim(),
        email: form.email.trim(),
        firm: form.firm.trim() || undefined,
        phone: form.phone.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      onCount();
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="border border-emerald-400/40 bg-emerald-500/10 backdrop-blur-md rounded-2xl p-8 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-400" />
        <p className="mt-4 text-base font-semibold">You're registered.</p>
        <p className="mt-2 text-sm text-zinc-400">
          A confirmation email is on its way to <span className="text-white font-medium">{form.email}</span>.
          We'll send a reminder closer to the day.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-zinc-600/60 bg-[#363648] backdrop-blur-md rounded-2xl p-6 shadow-xl shadow-black/20">
      <p className="text-[11px] tracking-[0.4em] uppercase text-[#c4b5fd] mb-2">{isFull ? 'Workshop full' : 'Reserve your seat'}</p>
      <p className="text-lg font-semibold mb-5">{isFull ? 'Join the waitlist' : 'Register · free'}</p>
      <form onSubmit={submit} className="space-y-3">
        <Field icon={User} value={form.name} onChange={v => update('name', v)} placeholder="Your name" required />
        <Field icon={Mail} type="email" value={form.email} onChange={v => update('email', v)} placeholder="Work email" required />
        <Field icon={Building2} value={form.firm} onChange={v => update('firm', v)} placeholder="Firm (optional)" />
        <Field icon={Phone} type="tel" value={form.phone} onChange={v => update('phone', v)} placeholder="Phone (optional)" />
        <div className="relative">
          <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-zinc-400" />
          <textarea
            value={form.notes}
            onChange={e => update('notes', e.target.value)}
            placeholder="Anything to ask the presenter? (optional)"
            rows={3}
            className="w-full pl-10 pr-3 py-2.5 bg-[#1d1d28] border border-zinc-600/60 rounded-xl text-sm text-white placeholder:text-zinc-400 focus:border-[#7c75f0] focus:outline-none resize-none"
          />
        </div>

        {error && (
          <div className="flex items-start gap-2 border border-rose-500/30 bg-rose-500/10 rounded-xl p-3 text-xs text-rose-200">
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className={cn(
            'inline-flex items-center justify-center gap-2 w-full bg-[#5048e5] hover:bg-[#5f58e8] disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[11px] tracking-[0.15em] uppercase px-6 py-3 rounded-full transition',
          )}
        >
          {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Registering…</> : isFull ? 'Join waitlist' : 'Register now'}
          {!submitting && <ArrowRight className="h-3.5 w-3.5" />}
        </button>
        <p className="text-[10px] text-zinc-600 text-center">Free · we'll email the joining details and a reminder.</p>
      </form>
    </div>
  );
}

function Field({ icon: Icon, value, onChange, placeholder, type = 'text', required }: {
  icon: any; value: string; onChange: (v: string) => void; placeholder: string; type?: string; required?: boolean;
}) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full pl-10 pr-3 py-2.5 bg-[#1d1d28] border border-zinc-600/60 rounded-xl text-sm text-white placeholder:text-zinc-400 focus:border-[#7c75f0] focus:outline-none"
      />
    </div>
  );
}
